import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ChevronDown, ChevronUp, ArrowRight, Check, XIcon } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
const CHECKOUT_URL = 'https://weh2ks-1j.myshopify.com/cart/47705293717762:1';
const CTAButton = ({
  text
}: {
  text: string;
}) => <motion.a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 text-lg md:text-xl font-bold text-white bg-orange-500 hover:bg-orange-600 rounded-xl shadow-[0_4px_14px_0_rgba(249,115,22,0.39)] hover:shadow-[0_6px_20px_rgba(249,115,22,0.23)] transition-all duration-200 text-center cursor-pointer no-underline" data-id="element-1">
    {text}
  </motion.a>;
export function App() {
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | null>(null);
  return <div className="min-h-screen bg-white text-slate-900 selection:bg-orange-200 selection:text-orange-900" data-id="element-2">
      {/* SECTION 1: HERO */}
      <section className="bg-slate-900 text-white pt-16 pb-20 px-4 overflow-hidden relative" data-id="element-3">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-slate-900" data-id="element-4"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-5">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-6">
            <div className="flex-1 text-center md:text-left" data-id="element-7">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-8">
                <div className="inline-block px-4 py-1.5 rounded-full bg-orange-500/20 text-orange-400 font-semibold text-sm mb-6 border border-orange-500/30" data-id="element-9">
                  Lanzamiento Especial
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight" data-id="element-10">
                  Amazon FBA{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600" data-id="element-11">
                    First Sale System™
                  </span>
                </h1>
                <p className="text-xl md:text-2xl text-slate-300 font-medium mb-8 leading-relaxed" data-id="element-12">
                  El sistema paso a paso para encontrar tu primer producto,
                  empezar con poco presupuesto y acercarte a tu primera venta en
                  solo 7 días.
                </p>

                <ul className="space-y-4 mb-10 text-left text-slate-300" data-id="element-13">
                  {['Diseñado específicamente para principiantes desde cero.', 'Enfoque en presupuestos bajos ($100 a $500).', 'Sin estrategias complejas ni herramientas costosas.', 'Acceso inmediato tras la compra.'].map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-14">
                      <CheckCircle2 className="w-6 h-6 text-orange-500 shrink-0 mt-0.5" data-id="element-15" />
                      <span className="text-lg" data-id="element-16">{bullet}</span>
                    </li>)}
                </ul>

                <CTAButton text="👉 QUIERO MI ACCESO AHORA POR SOLO $9.99" data-id="element-17" />
              </motion.div>
            </div>

            <motion.div className="flex-1 w-full max-w-md" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} data-id="element-18">
              <div className="relative" data-id="element-19">
                <div className="absolute -inset-4 bg-orange-500/20 blur-2xl rounded-full" data-id="element-20"></div>
                <img src="https://cdn.magicpatterns.com/uploads/62o7Qn26ULGowdp4NnfT3v/heylloyd-image-amazon_firt_.png" alt="Amazon FBA First Sale System Mockup" className="relative z-10 w-full h-auto drop-shadow-2xl" data-id="element-21" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2: EL PROBLEMA */}
      <section className="py-20 px-4 bg-white" data-id="element-22">
        <div className="max-w-3xl mx-auto" data-id="element-23">
          <FadeIn data-id="element-24">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-slate-900" data-id="element-25">
              ¿Quieres empezar en Amazon FBA pero te sientes paralizado por la
              confusión?
            </h2>
            <div className="prose prose-lg md:prose-xl mx-auto text-slate-600 space-y-6" data-id="element-26">
              <p data-id="element-27">
                Pasa todos los días. Ves videos en YouTube, sigues a "gurús" en
                TikTok y lees blogs, pero al final del día...{' '}
                <strong data-id="element-28">sigues sin saber cuál es el primer paso real.</strong>
              </p>
              <p data-id="element-29">
                Tienes miedo de elegir un producto que no se venda, miedo de
                perder tus ahorros y miedo de que el proceso sea demasiado
                complejo para ti.
              </p>
              <p data-id="element-30">
                La mayoría de los cursos hoy en día están diseñados para gente
                que tiene $5,000 para invertir. Pero,{' '}
                <em data-id="element-31">
                  ¿qué pasa si tú quieres empezar con poco y aprender el modelo
                  de forma segura?
                </em>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 3: LA SOLUCIÓN */}
      <section className="py-20 px-4 bg-slate-50 border-y border-slate-100" data-id="element-32">
        <div className="max-w-3xl mx-auto" data-id="element-33">
          <FadeIn data-id="element-34">
            <div className="text-center mb-10" data-id="element-35">
              <span className="text-orange-600 font-bold tracking-wider uppercase text-sm" data-id="element-36">
                La Solución
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6 text-slate-900" data-id="element-37">
                Presentamos: Amazon FBA First Sale System™
              </h2>
            </div>

            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-slate-200" data-id="element-38">
              <p className="text-lg text-slate-700 mb-6 leading-relaxed" data-id="element-39">
                He simplificado todo el ruido exterior para entregarte una ruta
                clara. Este no es un curso teórico de 40 horas; es un{' '}
                <strong data-id="element-40">sistema de acción rápido</strong> diseñado para que
                entiendas el negocio, filtres tus ideas y sepas exactamente qué
                hacer cada día de tu primera semana.
              </p>

              <div className="bg-orange-50 p-6 rounded-xl border border-orange-100 mt-8" data-id="element-41">
                <h3 className="text-xl font-bold text-orange-900 mb-3 flex items-center gap-2" data-id="element-42">
                  <span className="text-2xl" data-id="element-43">🎯</span> El Mecanismo Único
                </h3>
                <p className="text-orange-800 leading-relaxed" data-id="element-44">
                  Usamos el <strong data-id="element-45">Criterio de Riesgo Mínimo</strong>: No
                  buscamos el "producto millonario" de entrada. Buscamos tu
                  primera venta. Esa venta que te da la confianza y el
                  conocimiento para escalar de verdad.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 4: QUÉ INCLUYE EL SISTEMA */}
      <section className="py-20 px-4 bg-white" data-id="element-46">
        <div className="max-w-3xl mx-auto" data-id="element-47">
          <FadeIn data-id="element-48">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-slate-900" data-id="element-49">
              ¿Qué incluye el sistema?
            </h2>

            <div className="space-y-4" data-id="element-50">
              {[{
              title: 'Capítulo 1-3',
              desc: 'Los fundamentos reales de Amazon FBA (sin rodeos).'
            }, {
              title: 'Capítulo 4',
              desc: 'Cómo validar ideas de producto con sentido común (y sin herramientas caras).'
            }, {
              title: 'Capítulo 5',
              desc: 'Los errores "quiebra-cuentas" que debes evitar a toda costa.'
            }, {
              title: 'Capítulo 6',
              desc: 'Tu Plan de Acción de 7 Días (Tu mapa diario).'
            }, {
              title: 'Capítulo 7-8',
              desc: 'Cómo mantener el impulso y qué hacer después de lanzar.'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex flex-col sm:flex-row gap-4 p-6 bg-slate-50 rounded-xl border border-slate-100 hover:border-orange-200 transition-colors" data-id="element-51">
                  <div className="sm:w-32 shrink-0 font-bold text-orange-600" data-id="element-52">
                    {item.title}
                  </div>
                  <div className="text-slate-700 text-lg" data-id="element-53">{item.desc}</div>
                </motion.div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 5: LOS BONOS GRATUITOS */}
      <section className="py-20 px-4 bg-slate-900 text-white" data-id="element-54">
        <div className="max-w-3xl mx-auto" data-id="element-55">
          <FadeIn data-id="element-56">
            <div className="text-center mb-12" data-id="element-57">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-58">
                Si ordenas hoy, te llevas estos 2 recursos extra (Gratis)
              </h2>
              <p className="text-xl text-slate-400" data-id="element-59">
                Herramientas prácticas para acelerar tu proceso.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-60">
              <div className="bg-slate-800 p-8 rounded-2xl border border-slate-700 relative overflow-hidden" data-id="element-61">
                <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-62">
                  VALOR: $9.00
                </div>
                <div className="text-4xl mb-4" data-id="element-63">🎁</div>
                <h3 className="text-xl font-bold mb-3 text-orange-400" data-id="element-64">
                  BONO 1: Checklist de Inicio Rápido
                </h3>
                <p className="text-slate-300" data-id="element-65">
                  No te olvides de ningún paso técnico ni legal. Imprímelo y
                  marca tu progreso.
                </p>
              </div>

              <div className="bg-slate-800 p-8 rounded-2xl border border-slate-700 relative overflow-hidden" data-id="element-66">
                <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-67">
                  VALOR: $7.00
                </div>
                <div className="text-4xl mb-4" data-id="element-68">🎁</div>
                <h3 className="text-xl font-bold mb-3 text-orange-400" data-id="element-69">
                  BONO 2: Hoja de Evaluación de Productos
                </h3>
                <p className="text-slate-300" data-id="element-70">
                  Mi plantilla personal para comparar ideas. Si los números no
                  dan, la hoja te lo dirá antes de que gastes un solo dólar.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 6: PARA QUIÉN ES */}
      <section className="py-20 px-4 bg-white" data-id="element-71">
        <div className="max-w-4xl mx-auto" data-id="element-72">
          <FadeIn data-id="element-73">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-slate-900" data-id="element-74">
              ¿Es este sistema para ti?
            </h2>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-75">
              <div className="bg-green-50 p-8 rounded-2xl border border-green-100" data-id="element-76">
                <h3 className="text-xl font-bold text-green-900 mb-6 flex items-center gap-2" data-id="element-77">
                  <CheckCircle2 className="text-green-600" data-id="element-78" />
                  Este sistema es para ti si:
                </h3>
                <ul className="space-y-4" data-id="element-79">
                  {['Eres principiante absoluto y no sabes por dónde empezar.', 'Tienes un presupuesto limitado y quieres protegerlo.', 'Buscas una guía práctica, corta y directa al grano.', 'Quieres resultados claros en lugar de teoría infinita.'].map((item, i) => <li key={i} className="flex gap-3 text-green-800" data-id="element-80">
                      <span className="text-green-600 mt-1" data-id="element-81">✅</span>
                      <span data-id="element-82">{item}</span>
                    </li>)}
                </ul>
              </div>

              <div className="bg-red-50 p-8 rounded-2xl border border-red-100" data-id="element-83">
                <h3 className="text-xl font-bold text-red-900 mb-6 flex items-center gap-2" data-id="element-84">
                  <XCircle className="text-red-600" data-id="element-85" />
                  Este sistema NO es para ti si:
                </h3>
                <ul className="space-y-4" data-id="element-86">
                  {['Buscas una fórmula "mágica" para hacerte rico mañana sin trabajar.', 'Ya eres un vendedor avanzado de Amazon (esto es nivel básico/intermedio).'].map((item, i) => <li key={i} className="flex gap-3 text-red-800" data-id="element-87">
                      <span className="text-red-600 mt-1" data-id="element-88">❌</span>
                      <span data-id="element-89">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 7: OFERTA FINAL */}
      <section id="oferta-final" className="py-20 px-4 bg-slate-50 border-t border-slate-200" data-id="element-90">
        <div className="max-w-2xl mx-auto text-center" data-id="element-91">
          <FadeIn data-id="element-92">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-slate-200 relative overflow-hidden" data-id="element-93">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-400 to-orange-600" data-id="element-94"></div>

              <h2 className="text-3xl font-bold mb-8 text-slate-900" data-id="element-95">
                Resumen de tu orden
              </h2>

              <div className="space-y-4 mb-8 text-left" data-id="element-96">
                <div className="flex justify-between items-center pb-4 border-b border-slate-100" data-id="element-97">
                  <span className="text-slate-700 font-medium" data-id="element-98">
                    Amazon FBA First Sale System™
                  </span>
                  <span className="text-slate-400 line-through" data-id="element-99">$29.99</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-slate-100" data-id="element-100">
                  <span className="text-slate-700 font-medium" data-id="element-101">
                    Checklist de Inicio Rápido
                  </span>
                  <span className="text-slate-400 line-through" data-id="element-102">$9.00</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-slate-100" data-id="element-103">
                  <span className="text-slate-700 font-medium" data-id="element-104">
                    Hoja de Evaluación
                  </span>
                  <span className="text-slate-400 line-through" data-id="element-105">$7.00</span>
                </div>
                <div className="flex justify-between items-center pt-2" data-id="element-106">
                  <span className="text-slate-900 font-bold" data-id="element-107">Valor Total:</span>
                  <span className="text-slate-900 font-bold line-through text-xl" data-id="element-108">
                    $45.99
                  </span>
                </div>
              </div>

              <div className="bg-orange-50 rounded-2xl p-6 mb-8 border border-orange-100" data-id="element-109">
                <p className="text-orange-900 font-medium mb-2 uppercase tracking-wide text-sm" data-id="element-110">
                  Llévatelo todo hoy por solo:
                </p>
                <div className="text-6xl font-extrabold text-orange-600 mb-2" data-id="element-111">
                  $9.99
                </div>
                <p className="text-orange-700 text-sm" data-id="element-112">
                  Pago único. Acceso inmediato.
                </p>
              </div>

              <div id="cta-final" data-id="element-113">
                <CTAButton text="👉 SÍ, QUIERO EMPEZAR MI NEGOCIO HOY" data-id="element-114" />
              </div>

              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500" data-id="element-115">
                <Check className="w-4 h-4 text-green-500" data-id="element-116" />
                <span data-id="element-117">Garantía de satisfacción. Pago 100% seguro.</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 8: PREGUNTAS FRECUENTES (FAQ) */}
      <section className="py-20 px-4 bg-white" data-id="element-118">
        <div className="max-w-3xl mx-auto" data-id="element-119">
          <FadeIn data-id="element-120">
            <h2 className="text-3xl font-bold text-center mb-12 text-slate-900" data-id="element-121">
              Preguntas Frecuentes
            </h2>

            <div className="space-y-4" data-id="element-122">
              {[{
              q: '¿Es un curso en video?',
              a: 'No, es un sistema digital en formato PDF diseñado para ser consumido rápido y puesto en práctica de inmediato.'
            }, {
              q: '¿Cuánto dinero necesito para los productos?',
              a: 'El sistema está optimizado para personas que quieren empezar con un inventario inicial de entre $100 y $500.'
            }, {
              q: '¿Recibo acceso al instante?',
              a: 'Sí. Una vez completado el pago, recibirás un enlace de descarga en tu correo de forma automática.'
            }].map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} data-id="element-123" />)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-500 py-8 text-center text-sm" data-id="element-124">
        <div className="max-w-3xl mx-auto px-4" data-id="element-125">
          <div className="flex items-center justify-center gap-4 mb-4" data-id="element-126">
            <button onClick={() => setLegalModal('privacy')} className="text-slate-400 hover:text-white transition-colors underline underline-offset-2" data-id="element-127">
              Privacy Policy
            </button>
            <span className="text-slate-700" data-id="element-128">|</span>
            <button onClick={() => setLegalModal('terms')} className="text-slate-400 hover:text-white transition-colors underline underline-offset-2" data-id="element-129">
              Terms & Conditions
            </button>
          </div>
          <p data-id="element-130">
            © {new Date().getFullYear()} Amazon FBA First Sale System™. Todos
            los derechos reservados.
          </p>
          <p className="mt-2 text-xs opacity-70" data-id="element-131">
            Este sitio no es parte del sitio web de Amazon ni de Amazon.com Inc.
            Además, este sitio NO está respaldado por Amazon de ninguna manera.
            AMAZON es una marca registrada de AMAZON.COM INC.
          </p>
        </div>
      </footer>

      {/* Legal Modals */}
      <AnimatePresence data-id="element-132">
        {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} data-id="element-133" />}
      </AnimatePresence>
    </div>;
}
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-134">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.25
    }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-135">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50" data-id="element-136">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-137">
            {type === 'privacy' ? 'Privacy Policy' : 'Terms & Conditions'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors" data-id="element-138">
            <XIcon className="w-5 h-5 text-slate-600" data-id="element-139" />
          </button>
        </div>
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-slate-700 text-sm leading-relaxed space-y-4" data-id="element-140">
          {type === 'privacy' ? <PrivacyContent data-id="element-141" /> : <TermsContent data-id="element-142" />}
        </div>
      </motion.div>
    </motion.div>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-slate-500" data-id="element-143">Última actualización: Abril 2026</p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-144">
        1. Información que Recopilamos
      </h3>
      <p data-id="element-145">
        Cuando realizas una compra en nuestro sitio, recopilamos la información
        necesaria para procesar tu pedido, incluyendo tu nombre, dirección de
        correo electrónico e información de pago. No almacenamos datos de
        tarjetas de crédito directamente; estos son procesados de forma segura
        por nuestro proveedor de pagos.
      </p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-146">
        2. Uso de la Información
      </h3>
      <p data-id="element-147">
        Utilizamos tu información personal exclusivamente para: procesar y
        entregar tu compra digital, enviarte el enlace de descarga del producto,
        y comunicarnos contigo en caso de ser necesario respecto a tu pedido.
      </p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-148">
        3. Protección de Datos
      </h3>
      <p data-id="element-149">
        Implementamos medidas de seguridad estándar de la industria para
        proteger tu información personal. Tu información no será vendida,
        intercambiada ni transferida a terceros sin tu consentimiento, excepto
        cuando sea necesario para completar tu transacción.
      </p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-150">4. Cookies</h3>
      <p data-id="element-151">
        Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
        navegación y con fines analíticos. Puedes desactivar las cookies en la
        configuración de tu navegador.
      </p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-152">
        5. Productos Digitales
      </h3>
      <p data-id="element-153">
        Al completar tu compra, recibirás acceso inmediato al producto digital a
        través de un enlace de descarga enviado a tu correo electrónico. Eres
        responsable de mantener la seguridad de tu enlace de descarga.
      </p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-154">
        6. Cambios a esta Política
      </h3>
      <p data-id="element-155">
        Nos reservamos el derecho de actualizar esta política de privacidad en
        cualquier momento. Los cambios serán publicados en esta página con la
        fecha de actualización correspondiente.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-slate-500" data-id="element-156">Última actualización: Abril 2026</p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-157">
        1. Aceptación de Términos
      </h3>
      <p data-id="element-158">
        Al acceder y utilizar este sitio web y al realizar una compra, aceptas
        estar sujeto a estos términos y condiciones. Si no estás de acuerdo con
        alguna parte de estos términos, no debes utilizar nuestro sitio ni
        adquirir nuestros productos.
      </p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-159">2. Producto Digital</h3>
      <p data-id="element-160">
        Amazon FBA First Sale System™ es un producto digital en formato PDF. Al
        completar la compra, recibirás acceso inmediato al material a través de
        un enlace de descarga. El contenido es únicamente para uso educativo e
        informativo.
      </p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-161">3. Uso del Producto</h3>
      <p data-id="element-162">
        El producto adquirido es para uso personal únicamente. Queda
        estrictamente prohibido reproducir, distribuir, modificar, exhibir
        públicamente o crear obras derivadas del contenido sin autorización
        previa por escrito.
      </p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-163">4. Resultados</h3>
      <p data-id="element-164">
        No garantizamos resultados específicos de ventas o ingresos. Los
        resultados varían según el esfuerzo individual, las condiciones del
        mercado y otros factores. El contenido proporcionado es educativo y no
        constituye asesoría financiera o empresarial.
      </p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-165">
        5. Política de Reembolso
      </h3>
      <p data-id="element-166">
        Debido a la naturaleza digital del producto y al acceso inmediato
        proporcionado tras la compra, todas las ventas son finales. No se
        ofrecen reembolsos una vez que el producto ha sido entregado.
      </p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-167">
        6. Propiedad Intelectual
      </h3>
      <p data-id="element-168">
        Todo el contenido, incluyendo textos, gráficos, logos y materiales
        descargables, es propiedad de Amazon FBA First Sale System™ y está
        protegido por las leyes de propiedad intelectual aplicables.
      </p>
      <h3 className="text-lg font-bold text-slate-900" data-id="element-169">
        7. Descargo de Responsabilidad
      </h3>
      <p data-id="element-170">
        Este producto no está afiliado, asociado, autorizado, respaldado ni de
        ninguna manera conectado oficialmente con Amazon.com, Inc. o cualquiera
        de sus subsidiarias o afiliados. Amazon es una marca registrada de
        Amazon.com, Inc.
      </p>
    </>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50" data-id="element-171">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none bg-white hover:bg-slate-50 transition-colors" data-id="element-172">
        <span className="font-bold text-slate-900 text-lg pr-8" data-id="element-173">
          {question}
        </span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-500 shrink-0" data-id="element-174" /> : <ChevronDown className="w-5 h-5 text-slate-500 shrink-0" data-id="element-175" />}
      </button>
      <AnimatePresence data-id="element-176">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-177">
            <div className="px-6 pb-5 pt-2 text-slate-600 text-lg border-t border-slate-100" data-id="element-178">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}